import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Form, Nav } from 'react-bootstrap';
import { RefreshCw } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { useMcpStore } from '@/frontend/store/McpStore';
import { McpActionStatus, McpChangesetParams, McpProposedActionParams } from '@/types/mcpTypes';
import { Breadcrumbs } from '../Breadcrumbs';
import { TwoPanelPlaceholder } from '../TwoPanelPlaceholder';
import { McpStatusBadge } from './McpStatusBadge';

const POLL_INTERVAL_MS = 15_000;
// While approved changes are still running in the background.
const EXECUTING_POLL_INTERVAL_MS = 3_000;

const isPending = (action: McpProposedActionParams) => action.status === McpActionStatus.PENDING;

const formatDateTime = (value: Date | string) => new Date(value).toLocaleString();

/** The query and body exactly as they will be sent, or null when the request has neither. */
const requestFields = (action: McpProposedActionParams) => {
  const fields = {
    ...(action.query && Object.keys(action.query).length > 0 ? { query: action.query } : {}),
    ...(action.body !== null && action.body !== undefined ? { body: action.body } : {}),
  };
  return Object.keys(fields).length > 0 ? JSON.stringify(fields, null, 2) : null;
};

const preStyle = { maxHeight: '18rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } as const;

const isDelete = (action: McpProposedActionParams) => action.method === 'DELETE';

/** Pending changes by HTTP method, deletions first, so one deletion among many edits is seen before "select all". */
const pendingByMethod = (pending: McpProposedActionParams[]) => {
  const counts = new Map<string, number>();
  for (const action of pending) {
    counts.set(action.method, (counts.get(action.method) ?? 0) + 1);
  }
  return [...counts.entries()].sort(([a], [b]) => Number(b === 'DELETE') - Number(a === 'DELETE') || a.localeCompare(b));
};

/*
 * The request is what runs, so it is always on screen. The summary is the
 * agent's own description of it and is shown as such, never in its place.
 */
function ActionRow({ action, selected, disabled, onToggle }: {
  action: McpProposedActionParams;
  selected: boolean;
  disabled: boolean;
  onToggle: (actionId: string) => void;
}) {
  const [showResponse, setShowResponse] = useState(false);
  const fields = requestFields(action);
  const hasResponse = action.response !== null && action.response !== undefined;

  return (
    <Fragment>
      <tr className={selected ? 'table-active' : undefined}>
        <td className="ps-3" style={{ width: '2.5rem' }}>
          {isPending(action) && (
            <Form.Check
              type="checkbox"
              aria-label={action.summary}
              checked={selected}
              disabled={disabled}
              onChange={() => onToggle(action.id)}
            />
          )}
        </td>
        <td className="text-muted" style={{ width: '2rem' }}>{action.sequence}</td>
        <td>
          <div className="d-flex flex-column gap-1">
            <div>
              {isDelete(action) && <Badge bg="danger" className="me-2">{I18n.get('aiDeletes')}</Badge>}
              <code className={isDelete(action) ? 'text-danger' : undefined}>{action.method} {action.path}</code>
              <span className="text-muted small"> · {action.operationId}</span>
            </div>
            {fields && <pre className="mb-0 p-2 bg-light rounded small overflow-auto" style={preStyle}>{fields}</pre>}
            <div className="small text-muted">
              {I18n.get('aiAgentSummary')} <span className="fst-italic">{action.summary}</span>
            </div>
            {action.errorMessage && <div className="small text-danger">{action.errorMessage}</div>}
          </div>
        </td>
        <td style={{ width: '7rem' }}><McpStatusBadge status={action.status} /></td>
        <td className="pe-3 text-end" style={{ width: '9rem' }}>
          {hasResponse && (
            <Button variant="link" size="sm" className="p-0" onClick={() => setShowResponse(!showResponse)}>
              {I18n.get(showResponse ? 'aiHideResponse' : 'aiShowResponse')}
            </Button>
          )}
        </td>
      </tr>
      {showResponse && (
        <tr>
          <td colSpan={5} className="px-3">
            <pre className="mb-0 p-2 bg-light rounded small overflow-auto" style={preStyle}>
              {JSON.stringify(action.response, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function ChangesetCard({ changeset, selected, disabled, onToggle, onToggleAll, highlighted }: {
  changeset: McpChangesetParams;
  selected: Set<string>;
  disabled: boolean;
  onToggle: (actionId: string) => void;
  onToggleAll: (changeset: McpChangesetParams, select: boolean) => void;
  highlighted: boolean;
}) {
  const pending = changeset.actions.filter(isPending);
  const allSelected = pending.length > 0 && pending.every((action) => selected.has(action.id));

  return (
    <div id={`changeset-${changeset.id}`} className={`card ${highlighted ? 'border-primary border-2' : ''}`}>
      <div className="card-header d-flex flex-wrap align-items-center gap-3">
        {pending.length > 0 && (
          <Form.Check
            type="checkbox"
            aria-label={I18n.get('aiSelectAllPending')}
            title={I18n.get('aiSelectAllPending')}
            checked={allSelected}
            disabled={disabled}
            onChange={() => onToggleAll(changeset, !allSelected)}
          />
        )}
        <span className="fw-semibold">{changeset.title}</span>
        <span className="small text-muted">{I18n.get('aiProposedAt', { date: formatDateTime(changeset.createdAt) })}</span>
        {pending.length > 0 && (
          <span className="small d-flex gap-2" aria-label={I18n.get('aiPendingByMethod')} title={I18n.get('aiPendingByMethod')}>
            {pendingByMethod(pending).map(([method, count]) => (
              <Badge key={method} bg={method === 'DELETE' ? 'danger' : 'secondary'}>{count} {method}</Badge>
            ))}
          </span>
        )}
      </div>
      <div className="table-responsive">
        <table className="table mb-0 align-top">
          <tbody>
            {changeset.actions.map((action) => (
              <ActionRow key={action.id} action={action} selected={selected.has(action.id)} disabled={disabled} onToggle={onToggle} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Where the user reviews what their AI assistant wants to change, and approves
 * or rejects it -- one change, a whole changeset, or everything at once.
 */
export function AiApprovalsPage() {
  const changesets = useMcpStore((state) => state.changesets);
  const scope = useMcpStore((state) => state.changesetScope);
  const fetchChangesets = useMcpStore((state) => state.fetchChangesets);
  const fetchChangeset = useMcpStore((state) => state.fetchChangeset);
  const approve = useMcpStore((state) => state.approve);
  const reject = useMcpStore((state) => state.reject);
  const reset = useMcpStore((state) => state.reset);
  const [searchParams] = useSearchParams();
  const highlightedId = searchParams.get('changeset');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // True while approve/reject requests are in flight.
  const [deciding, setDeciding] = useState(false);
  // A failed background poll pauses polling until the user refreshes, so a
  // dropped connection shows one error rather than one every few seconds.
  const [pollFailed, setPollFailed] = useState(false);

  useEffect(() => {
    void fetchChangesets('open').then((ok) => {
      setPollFailed(!ok);
      setLoading(false);
    });
    return () => reset();
  }, [fetchChangesets, reset]);

  // New proposals arrive while the user has the page open.
  useEffect(() => {
    if (scope !== 'open' || pollFailed) {
      return;
    }
    const interval = setInterval(() => {
      if (!document.hidden) {
        void fetchChangesets().then((ok) => setPollFailed(!ok));
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [scope, pollFailed, fetchChangesets]);

  // Large approved batches finish in the background. Follow just those
  // changesets until they are done, rather than refetching the whole list.
  const executingIds = changesets
    .filter((changeset) => changeset.actions.some((action) => action.status === McpActionStatus.EXECUTING))
    .map((changeset) => changeset.id)
    .join(',');
  useEffect(() => {
    if (!executingIds || pollFailed) {
      return;
    }
    const interval = setInterval(() => {
      if (!document.hidden) {
        void Promise.all(executingIds.split(',').map(fetchChangeset)).then((results) => setPollFailed(!results.every(Boolean)));
      }
    }, EXECUTING_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [executingIds, pollFailed, fetchChangeset]);

  useEffect(() => {
    if (!loading && highlightedId) {
      document.getElementById(`changeset-${highlightedId}`)?.scrollIntoView({ block: 'start' });
    }
  }, [loading, highlightedId]);

  // Drop selections that are no longer pending (decided elsewhere, or expired).
  const pendingIds = useMemo(
    () => new Set(changesets.flatMap((changeset) => changeset.actions.filter(isPending).map((action) => action.id))),
    [changesets],
  );
  const selectedPending = [...selected].filter((id) => pendingIds.has(id));

  const toggle = (actionId: string) => {
    const next = new Set(selected);
    if (next.has(actionId)) {
      next.delete(actionId);
    } else {
      next.add(actionId);
    }
    setSelected(next);
  };

  const toggleAll = (changeset: McpChangesetParams, select: boolean) => {
    const next = new Set(selected);
    for (const action of changeset.actions.filter(isPending)) {
      if (select) {
        next.add(action.id);
      } else {
        next.delete(action.id);
      }
    }
    setSelected(next);
  };

  const decideSelected = async (decision: typeof approve) => {
    // One request per changeset, in page order; each runs its actions in sequence.
    const batches = changesets
      .map((changeset) => ({
        changesetId: changeset.id,
        actionIds: changeset.actions.filter((action) => selected.has(action.id) && isPending(action)).map((action) => action.id),
      }))
      .filter(({ actionIds }) => actionIds.length > 0);
    setDeciding(true);
    try {
      for (const { changesetId, actionIds } of batches) {
        await decision(changesetId, actionIds);
        // Only what was sent: anything left selected is still the user's choice.
        setSelected((previous) => new Set([...previous].filter((id) => !actionIds.includes(id))));
      }
    } finally {
      setDeciding(false);
    }
  };

  const refresh = () => {
    void fetchChangesets().then((ok) => setPollFailed(!ok));
  };

  const switchScope = (next: 'open' | 'recent') => {
    setSelected(new Set());
    setLoading(true);
    void fetchChangesets(next).then((ok) => {
      setPollFailed(!ok);
      setLoading(false);
    });
  };

  return (
    <div className="d-flex flex-column">
      <Breadcrumbs items={[{ title: I18n.get('aiAssistant'), link: '/ai-assistant' }, { title: I18n.get('aiApprovals') }]} />
      <div className="p-4 d-flex flex-column gap-4">
        <div>
          <h1 className="card-title mb-1">{I18n.get('aiApprovals')}</h1>
          <p className="text-muted mb-0">{I18n.get('aiApprovalsDescription')}</p>
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <Nav variant="tabs" activeKey={scope} onSelect={(key) => switchScope(key === 'recent' ? 'recent' : 'open')}>
            <Nav.Item>
              <Nav.Link eventKey="open">{I18n.get('aiApprovalsPending')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="recent">{I18n.get('aiApprovalsHistory')}</Nav.Link>
            </Nav.Item>
          </Nav>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button variant="outline-secondary" className="d-flex align-items-center gap-1" onClick={refresh}>
              <RefreshCw size={16} /> {I18n.get('aiApprovalsRefresh')}
            </Button>
            <Button
              variant="outline-danger"
              disabled={deciding || selectedPending.length === 0}
              onClick={() => void decideSelected(reject)}
            >
              {I18n.get('aiRejectSelected', { count: String(selectedPending.length) })}
            </Button>
            <Button variant="primary" disabled={deciding || selectedPending.length === 0} onClick={() => void decideSelected(approve)}>
              {I18n.get('aiApproveSelected', { count: String(selectedPending.length) })}
            </Button>
          </div>
        </div>

        {pollFailed && <Alert variant="warning" className="mb-0">{I18n.get('aiApprovalsPollFailed')}</Alert>}

        {loading ? (
          <TwoPanelPlaceholder />
        ) : changesets.length === 0 ? (
          <p className="text-muted mb-0">{I18n.get(scope === 'open' ? 'aiApprovalsNone' : 'aiApprovalsNoHistory')}</p>
        ) : (
          <div className="d-flex flex-column gap-4">
            {changesets.map((changeset) => (
              <ChangesetCard
                key={changeset.id}
                changeset={changeset}
                selected={selected}
                disabled={deciding}
                onToggle={toggle}
                onToggleAll={toggleAll}
                highlighted={changeset.id === highlightedId}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
